import React, { useMemo } from 'react';
import type {
  IntegrationActivityEvent,
  PropertyPortalPublication,
} from 'src/api/generated';
import {
  EventTimeline,
  type EventTimelineItem,
} from 'src/modules/shared/components/molecules/event-timeline';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  buildIntegrationActivityEventTags,
  getIntegrationActivityEventLabelKey,
  resolveIntegrationActivityEventTone,
} from 'src/modules/shared/utils/integrations/integration-activity-event.util';
import {
  formatDate,
  formatTimeDistance,
} from 'src/modules/shared/utils/formatting/date.util';
interface PropertyPublicationHistoryProps {
  publication: PropertyPortalPublication | null;
  events?: IntegrationActivityEvent[];
}

interface PublicationHistoryItem extends EventTimelineItem {
  sortValue: string;
}

const formatHistoryValue = (value: string, language: string) => {
  const date = new Date(value);
  return {
    value: formatTimeDistance(date, true, language),
    valueTooltip: formatDate(value),
  };
};

export const PropertyPublicationHistory: React.FC<
  PropertyPublicationHistoryProps
> = ({ publication, events = [] }) => {
  const { t, language } = useI18n();

  const items = useMemo<PublicationHistoryItem[]>(() => {
    if (!publication) {
      return [];
    }

    if (events.length > 0) {
      return events
        .map((event) => {
          const timestamp = formatHistoryValue(event.createdAt, language);

          return {
            key: `${event.type}-${event.id}`,
            label: t(getIntegrationActivityEventLabelKey(event.type)),
            value: timestamp.value,
            valueTooltip: timestamp.valueTooltip,
            sortValue: event.createdAt,
            tone: resolveIntegrationActivityEventTone(event),
            tags: buildIntegrationActivityEventTags(event, t),
          };
        })
        .sort((left, right) => right.sortValue.localeCompare(left.sortValue));
    }

    const fallbackItems: (PublicationHistoryItem | null)[] = [
      (() => {
        const timestamp = formatHistoryValue(publication.createdAt, language);

        return {
          key: 'createdAt',
          label: t(
            'catalog.properties.meli.currentPublication.history.created'
          ),
          value: timestamp.value,
          valueTooltip: timestamp.valueTooltip,
          sortValue: publication.createdAt,
        };
      })(),
      publication.lastSyncAttemptAt
        ? (() => {
            const timestamp = formatHistoryValue(
              publication.lastSyncAttemptAt,
              language
            );

            return {
              key: 'lastSyncAttemptAt',
              label: t(
                'catalog.properties.meli.currentPublication.history.attempted'
              ),
              value: timestamp.value,
              valueTooltip: timestamp.valueTooltip,
              sortValue: publication.lastSyncAttemptAt,
            };
          })()
        : null,
      publication.lastSyncSuccessAt
        ? (() => {
            const timestamp = formatHistoryValue(
              publication.lastSyncSuccessAt,
              language
            );

            return {
              key: 'lastSyncSuccessAt',
              label: t(
                'catalog.properties.meli.currentPublication.history.synced'
              ),
              value: timestamp.value,
              valueTooltip: timestamp.valueTooltip,
              sortValue: publication.lastSyncSuccessAt,
              tone: 'success' as const,
            };
          })()
        : null,
      (() => {
        const timestamp = formatHistoryValue(publication.updatedAt, language);

        return {
          key: 'updatedAt',
          label: t(
            'catalog.properties.meli.currentPublication.history.updated'
          ),
          value: timestamp.value,
          valueTooltip: timestamp.valueTooltip,
          sortValue: publication.updatedAt,
        };
      })(),
      publication.nextSyncAt
        ? (() => {
            const timestamp = formatHistoryValue(
              publication.nextSyncAt,
              language
            );

            return {
              key: 'nextSyncAt',
              label: t(
                'catalog.properties.meli.currentPublication.history.nextSync'
              ),
              value: timestamp.value,
              valueTooltip: timestamp.valueTooltip,
              sortValue: publication.nextSyncAt,
              tone: 'warning' as const,
            };
          })()
        : null,
      publication.lastErrorAt && publication.lastErrorMessage
        ? (() => {
            const timestamp = formatHistoryValue(
              publication.lastErrorAt,
              language
            );

            return {
              key: 'lastErrorAt',
              label: t(
                'catalog.properties.meli.currentPublication.history.error'
              ),
              value: timestamp.value,
              valueTooltip: timestamp.valueTooltip,
              sortValue: publication.lastErrorAt,
              tone: 'danger' as const,
            };
          })()
        : null,
    ];

    return fallbackItems
      .filter((item): item is PublicationHistoryItem => item != null)
      .sort((left, right) => right.sortValue.localeCompare(left.sortValue));
  }, [events, language, publication, t]);

  if (!publication) {
    return null;
  }

  return (
    <EventTimeline
      items={items}
      emptyLabel={t('catalog.properties.meli.currentPublication.history.empty')}
    />
  );
};
